import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, RotateCcw } from 'lucide-react';

const SectionPanel = ({ 
  isActive, 
  onClose, 
  axis, 
  onAxisChange, 
  position, 
  onPositionChange,
  onReset 
}) => {
  if (!isActive) return null;

  const axisButtons = [
    { id: 'x', label: 'X' },
    { id: 'y', label: 'Y' },
    { id: 'z', label: 'Z' }
  ];

  return (
    <AnimatePresence>
      <motion.div
        className="fixed left-4 top-24 z-40"
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -20 }}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        data-testid="section-panel"
      >
        <div
          className="rounded-xl overflow-hidden shadow-2xl w-56"
          style={{
            background: 'rgba(10, 10, 25, 0.95)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.08)'
          }}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-white/10">
            <div className="flex items-center gap-2">
              <span className="text-cyan-400">✂</span>
              <span className="text-sm font-semibold text-white">Section Cut</span>
            </div>
            <button
              onClick={onClose}
              className="w-6 h-6 rounded flex items-center justify-center text-gray-400 hover:text-white hover:bg-white/10 transition-colors"
              data-testid="section-close-btn"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-4 space-y-4">
            {/* Axis Selection */}
            <div>
              <label className="text-xs text-gray-400 mb-2 block">Cut Axis</label>
              <div className="flex gap-2">
                {axisButtons.map(({ id, label }) => (
                  <button
                    key={id}
                    onClick={() => onAxisChange(id)}
                    className={`flex-1 px-3 py-1.5 rounded-full text-xs font-bold transition-all ${
                      axis === id
                        ? 'bg-cyan-500 text-black shadow-lg shadow-cyan-500/30'
                        : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white border border-white/10'
                    }`}
                    data-testid={`section-axis-${id}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Position Slider */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs text-gray-400">Position</label>
                <span className="text-xs font-mono text-cyan-400">{position.toFixed(1)}</span>
              </div>
              <input
                type="range"
                min="-3"
                max="3"
                step="0.1"
                value={position}
                onChange={(e) => onPositionChange(parseFloat(e.target.value))}
                className="w-full h-1.5 rounded-full appearance-none cursor-pointer"
                style={{
                  background: `linear-gradient(to right, #00d4ff ${((position + 3) / 6) * 100}%, rgba(255,255,255,0.1) ${((position + 3) / 6) * 100}%)`
                }}
                data-testid="section-position-slider"
              />
              <div className="flex justify-between text-[10px] text-gray-500 mt-1">
                <span>-3</span>
                <span>0</span>
                <span>+3</span>
              </div>
            </div>

            {/* Reset Button */}
            <button
              onClick={onReset}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white transition-colors text-xs"
              data-testid="section-reset-btn"
            >
              <RotateCcw className="w-3.5 h-3.5" />
              Reset to Center
            </button>
          </div>

          {/* Footer hint */}
          <div className="px-4 py-2 border-t border-white/5">
            <p className="text-[10px] text-gray-500 text-center">
              Slice through the model to reveal internal parts
            </p>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default SectionPanel;
